import * as React from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded'
import UploadRoundedIcon from '@mui/icons-material/UploadRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'

import AttackTemplateEditor, { normalizeAttackTemplateDraft } from './AttackTemplateEditor'
import useAutoTranslate from '../hooks/useAutoTranslate'
import { applyGamePresetToTemplateDraft } from '../utils/attackTemplateGamePreset'
import {
    buildSavedAttackTemplatesUrl,
    normalizeAttackTemplatePlatform
} from './attackTemplatePlatform'

const EMPTY_TEMPLATE = {}
const LIVE_IMPORT_PRESET_REFRESH_INTERVAL_MS = 1800 * 1000
const TEMPLATE_PLATFORMS = Object.freeze([
    { value: 'gge', label: 'GGE' },
    { value: 'e4k', label: 'E4K' }
])

const sanitizeTemplateFilename = value =>
    String(value ?? '')
        .trim()
        .split('')
        .map(character => {
            const code = character.charCodeAt(0)
            return code >= 0 && code < 32 ? '-' : character
        })
        .join('')
        .replace(/[<>:"/\\|?*]/g, '-')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')

const sortTemplatesByUpdatedAt = templates =>
    [...(Array.isArray(templates) ? templates : [])].sort((left, right) =>
        Number(right?.updatedAt ?? 0) - Number(left?.updatedAt ?? 0) ||
        String(left?.name ?? '').localeCompare(String(right?.name ?? ''))
    )

export const buildAttackTemplateSourceUserOptions = (users, usersStatus = {}) =>
    (Array.isArray(users) ? users : [])
        .map(user => {
            const userId = Number(user?.id ?? NaN)
            if (!Number.isFinite(userId) || userId <= 0)
                return undefined

            const name = String(user?.name ?? '').trim() || `Bot ${userId}`
            const runtimeStatus =
                usersStatus?.[userId] && typeof usersStatus[userId] === 'object' && !Array.isArray(usersStatus[userId]) ?
                    usersStatus[userId] :
                    {}

            return {
                id: userId,
                value: String(userId),
                name,
                platform: normalizeAttackTemplatePlatform(user?.platform),
                label: `${name}${Boolean(user?.state) ? '' : ' (offline)'}`,
                isOnline: Boolean(user?.state),
                inventory: Array.isArray(runtimeStatus?.attackTemplateInventory) ? runtimeStatus.attackTemplateInventory : [],
                availableAttackPresets: Array.isArray(runtimeStatus?.availableAttackPresets) ? runtimeStatus.availableAttackPresets : []
            }
        })
        .filter(Boolean)
        .sort((left, right) =>
            String(left?.name ?? '').localeCompare(String(right?.name ?? '')) ||
            Number(left?.id ?? 0) - Number(right?.id ?? 0))

const normalizeLivePresetImportOptions = presets =>
    (Array.isArray(presets) ? presets : [])
        .map(preset => ({
            value: String(preset?.value ?? ''),
            label: String(preset?.label ?? preset?.name ?? '').trim() || `Preset ${Number(preset?.value ?? 0) + 1}`,
            disabled: preset?.isAvailable === false
        }))
        .filter(option => option.value)

const sanitizePortableLibraryTemplate = template => ({
    ...template,
    presetSource: ''
})

export default function AttackTemplatesPage({ accountInfo, users = [], usersStatus = {}, appLanguage = 'en' }) {
    const [templates, setTemplates] = React.useState([])
    const [activeTemplatePlatform, setActiveTemplatePlatform] = React.useState('gge')
    const [loading, setLoading] = React.useState(true)
    const [feedback, setFeedback] = React.useState(null)
    const [searchTerm, setSearchTerm] = React.useState('')
    const deferredSearchTerm = React.useDeferredValue(searchTerm)
    const [editorOpen, setEditorOpen] = React.useState(false)
    const [editorMode, setEditorMode] = React.useState('create')
    const [editingTemplateId, setEditingTemplateId] = React.useState(null)
    const [draftTemplate, setDraftTemplate] = React.useState(EMPTY_TEMPLATE)
    const [saving, setSaving] = React.useState(false)
    const [deletingTemplateId, setDeletingTemplateId] = React.useState(null)
    const [movingTemplateId, setMovingTemplateId] = React.useState(null)
    const [selectedSourceUserId, setSelectedSourceUserId] = React.useState('')
    const [importPresetValue, setImportPresetValue] = React.useState('')
    const [importTargetWaveId, setImportTargetWaveId] = React.useState('')
    const [importPresets, setImportPresets] = React.useState([])
    const [loadingImportPresets, setLoadingImportPresets] = React.useState(false)
    const [importPresetError, setImportPresetError] = React.useState('')
    const importInputRef = React.useRef(null)
    const emptyInventory = React.useMemo(() => [], [])
    const t = useAutoTranslate(appLanguage, [
        'Attack Templates',
        'GGE Templates',
        'E4K Templates',
        'Search by name or user',
        'Create Template',
        'No description',
        'Saved by',
        'No templates yet',
        'Create your first attack template to see it here.',
        'Edit',
        'Delete',
        'Create Template',
        'Edit Template',
        'Close',
        'Cancel',
        'Save',
        'Save Template',
        'Template name is required.',
        'Could not load saved attack templates.',
        'Could not save attack template.',
        'Could not delete attack template.',
        'Could not move attack template.',
        'Attack template saved successfully.',
        'Attack template deleted.',
        'Attack template moved.',
        'Are you sure you want to delete this template?',
        'Move to GGE',
        'Move to E4K',
        'None',
        'Search templates',
        'Unknown user',
        'Visible templates',
        'Total templates',
        'Import',
        'Export',
        'Attack template exported.',
        'Imported template draft. Review it and press Save.',
        'Could not import attack template file.',
        'Source bot',
        'Load preset from game',
        'Import into wave',
        'Imported preset into the selected wave.',
        'Could not load presets from the game.',
        'Start this bot to load presets from the game.',
        'No bots available for preset import.'
    ])
    const normalizedDraftTemplate = React.useMemo(
        () => normalizeAttackTemplateDraft(draftTemplate),
        [draftTemplate]
    )
    const draftTemplateName = String(normalizedDraftTemplate?.name ?? '').trim()
    const sourceUserOptions = React.useMemo(
        () => buildAttackTemplateSourceUserOptions(users, usersStatus),
        [users, usersStatus]
    )
    const platformSourceUserOptions = React.useMemo(
        () => sourceUserOptions.filter(option => option.platform === activeTemplatePlatform),
        [activeTemplatePlatform, sourceUserOptions]
    )
    const selectedSourceUser = React.useMemo(
        () => platformSourceUserOptions.find(option => option.value === String(selectedSourceUserId)) ?? null,
        [platformSourceUserOptions, selectedSourceUserId]
    )
    const selectedSourceUserValue = String(selectedSourceUser?.value ?? '')
    const selectedSourceUserIsOnline = Boolean(selectedSourceUser?.isOnline)
    const selectedSourceUserInventory = selectedSourceUser?.isOnline ? selectedSourceUser.inventory : emptyInventory
    const noBotsAvailableForPresetImportMessage = t('No bots available for preset import.')
    const startBotToLoadPresetsMessage = t('Start this bot to load presets from the game.')
    const couldNotLoadPresetsMessage = t('Could not load presets from the game.')

    const renderFeedbackMessage = React.useCallback(item => {
        if (!item)
            return ''

        if (item.translate === false)
            return String(item.message ?? '')

        return t(item.messageKey ?? item.message ?? '')
    }, [t])

    const loadImportPresets = React.useCallback(async (sourceUser, options = {}) => {
        const {
            clearExistingOptions = true
        } = options

        if (!sourceUser?.value)
            return

        try {
            setLoadingImportPresets(true)
            setImportPresetError('')
            setImportPresetValue('')
            if (clearExistingOptions)
                setImportPresets([])

            const response = await fetch(`/attack-template-live-presets/${encodeURIComponent(sourceUser.value)}`, {
                credentials: 'same-origin'
            })
            const payload = await response.json().catch(() => ({}))

            if (!response.ok) {
                throw new Error(
                    response.status === 409 ?
                        startBotToLoadPresetsMessage :
                        String(payload?.error ?? couldNotLoadPresetsMessage)
                )
            }

            setImportPresets(Array.isArray(payload?.presets) ? payload.presets : [])
        }
        catch (error) {
            console.warn('Failed to load live attack presets', error)
            setImportPresets([])
            setImportPresetError(String(error?.message ?? couldNotLoadPresetsMessage))
        }
        finally {
            setLoadingImportPresets(false)
        }
    }, [couldNotLoadPresetsMessage, startBotToLoadPresetsMessage])

    const loadTemplates = React.useCallback(async () => {
        try {
            setLoading(true)
            setFeedback(null)

            const response = await fetch(buildSavedAttackTemplatesUrl(activeTemplatePlatform), {
                credentials: 'same-origin'
            })

            if (!response.ok)
                throw new Error(`Template list request failed with ${response.status}`)

            const payload = await response.json()
            setTemplates(sortTemplatesByUpdatedAt(payload?.templates))
        }
        catch (error) {
            console.warn('Failed to load saved attack templates', error)
            setFeedback({ severity: 'error', messageKey: 'Could not load saved attack templates.' })
        }
        finally {
            setLoading(false)
        }
    }, [activeTemplatePlatform])

    React.useEffect(() => {
        loadTemplates()
    }, [loadTemplates])

    const filteredTemplates = React.useMemo(() => {
        const normalizedQuery = String(deferredSearchTerm ?? '').trim().toLowerCase()

        if (!normalizedQuery)
            return templates

        return templates.filter(template => {
            const haystack = [
                template?.name,
                template?.description,
                template?.ownerUsername
            ].join(' ').toLowerCase()

            return haystack.includes(normalizedQuery)
        })
    }, [deferredSearchTerm, templates])

    const showOwnerName = React.useMemo(
        () => new Set(templates.map(template => String(template?.ownerUsername ?? '').trim()).filter(Boolean)).size > 1,
        [templates]
    )

    const presetOptions = React.useMemo(
        () => [{ label: t('None'), value: '' }],
        [t]
    )

    React.useEffect(() => {
        if (platformSourceUserOptions.length <= 0) {
            if (selectedSourceUserId)
                setSelectedSourceUserId('')
            return
        }

        const hasSelectedSourceUser = platformSourceUserOptions.some(option => option.value === String(selectedSourceUserId))
        if (!hasSelectedSourceUser)
            setSelectedSourceUserId(platformSourceUserOptions[0].value)
    }, [platformSourceUserOptions, selectedSourceUserId])

    React.useEffect(() => {
        if (!editorOpen) {
            setImportPresets([])
            setImportPresetValue('')
            setLoadingImportPresets(false)
            setImportPresetError('')
            return
        }

        if (!selectedSourceUserValue) {
            setImportPresets([])
            setImportPresetValue('')
            setLoadingImportPresets(false)
            setImportPresetError(platformSourceUserOptions.length > 0 ? '' : noBotsAvailableForPresetImportMessage)
            return
        }

        if (!selectedSourceUserIsOnline) {
            setImportPresets([])
            setImportPresetValue('')
            setLoadingImportPresets(false)
            setImportPresetError(startBotToLoadPresetsMessage)
            return
        }

        let ignore = false
        const importSourceUser = {
            value: selectedSourceUserValue
        }

        const guardedLoadImportPresets = async (options = {}) => {
            await loadImportPresets(importSourceUser, options)
            if (ignore)
                return
        }

        void guardedLoadImportPresets({ clearExistingOptions: true })
        const refreshInterval = window.setInterval(() => {
            void guardedLoadImportPresets({ clearExistingOptions: false })
        }, LIVE_IMPORT_PRESET_REFRESH_INTERVAL_MS)

        return () => {
            ignore = true
            window.clearInterval(refreshInterval)
        }
    }, [
        couldNotLoadPresetsMessage,
        editorOpen,
        loadImportPresets,
        noBotsAvailableForPresetImportMessage,
        selectedSourceUserIsOnline,
        selectedSourceUserValue,
        platformSourceUserOptions.length,
        startBotToLoadPresetsMessage
    ])

    React.useEffect(() => {
        const availableWaveIds = (normalizedDraftTemplate?.waves ?? []).map(wave => String(wave?.id ?? '')).filter(Boolean)
        if (availableWaveIds.length <= 0) {
            if (importTargetWaveId)
                setImportTargetWaveId('')
            return
        }

        if (!availableWaveIds.includes(String(importTargetWaveId))) {
            setImportTargetWaveId(
                availableWaveIds.includes(String(normalizedDraftTemplate?.defaultWaveId ?? '')) ?
                    String(normalizedDraftTemplate.defaultWaveId) :
                    availableWaveIds[0]
            )
        }
    }, [importTargetWaveId, normalizedDraftTemplate])

    const closeEditor = () => {
        setEditorOpen(false)
        setEditorMode('create')
        setEditingTemplateId(null)
        setDraftTemplate(EMPTY_TEMPLATE)
        setImportPresetValue('')
        setImportPresetError('')
        setImportPresets([])
    }

    const handleCreateTemplate = () => {
        setEditorMode('create')
        setEditingTemplateId(null)
        setDraftTemplate(EMPTY_TEMPLATE)
        setEditorOpen(true)
    }

    const handleEditTemplate = template => {
        setEditorMode('edit')
        setEditingTemplateId(template?.id ?? null)
        setDraftTemplate(template?.template ?? EMPTY_TEMPLATE)
        setEditorOpen(true)
    }

    const handleSaveTemplate = async () => {
        const normalizedTemplate = sanitizePortableLibraryTemplate(normalizeAttackTemplateDraft(draftTemplate))
        const templateName = String(normalizedTemplate?.name ?? '').trim()
        if (!templateName) {
            setFeedback({ severity: 'error', messageKey: 'Template name is required.' })
            return
        }

        try {
            setSaving(true)
            setFeedback(null)

            const response = await fetch(
                editingTemplateId ? `/saved-attack-templates/${editingTemplateId}` : '/saved-attack-templates',
                {
                    method: editingTemplateId ? 'PUT' : 'POST',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        platform: activeTemplatePlatform,
                        name: templateName,
                        description: String(normalizedTemplate?.description ?? ''),
                        template: normalizedTemplate
                    })
                }
            )

            const payload = await response.json().catch(() => ({}))
            if (!response.ok)
                throw new Error(payload?.error || `Template save request failed with ${response.status}`)

            const savedTemplate = payload?.template
            if (!savedTemplate)
                throw new Error('Missing saved template payload')

            setTemplates(previousTemplates => {
                const withoutCurrent = previousTemplates.filter(template => Number(template?.id) !== Number(savedTemplate.id))
                return sortTemplatesByUpdatedAt([savedTemplate, ...withoutCurrent])
            })
            setFeedback({ severity: 'success', messageKey: 'Attack template saved successfully.' })
            closeEditor()
        }
        catch (error) {
            console.warn('Failed to save attack template', error)
            setFeedback(error?.message ?
                { severity: 'error', message: error.message, translate: false } :
                { severity: 'error', messageKey: 'Could not save attack template.' })
        }
        finally {
            setSaving(false)
        }
    }

    const handleDeleteTemplate = async template => {
        if (!template)
            return

        if (!window.confirm(t('Are you sure you want to delete this template?')))
            return

        try {
            setDeletingTemplateId(template.id)
            setFeedback(null)

            const response = await fetch(`/saved-attack-templates/${template.id}`, {
                method: 'DELETE',
                credentials: 'same-origin'
            })
            const payload = await response.json().catch(() => ({}))
            if (!response.ok)
                throw new Error(payload?.error || `Template delete request failed with ${response.status}`)

            setTemplates(previousTemplates =>
                previousTemplates.filter(entry => Number(entry?.id) !== Number(template.id))
            )
            setFeedback({ severity: 'success', messageKey: 'Attack template deleted.' })
        }
        catch (error) {
            console.warn('Failed to delete attack template', error)
            setFeedback(error?.message ?
                { severity: 'error', message: error.message, translate: false } :
                { severity: 'error', messageKey: 'Could not delete attack template.' })
        }
        finally {
            setDeletingTemplateId(null)
        }
    }

    const handleMoveTemplatePlatform = async template => {
        if (!template)
            return

        const currentPlatform = normalizeAttackTemplatePlatform(template?.platform)
        const nextPlatform = currentPlatform === 'e4k' ? 'gge' : 'e4k'
        const normalizedTemplate = normalizeAttackTemplateDraft(template?.template ?? EMPTY_TEMPLATE)
        const templateName = String(template?.name ?? normalizedTemplate?.name ?? '').trim()
        const templateDescription = String(template?.description ?? normalizedTemplate?.description ?? '')
        const portableTemplate = sanitizePortableLibraryTemplate({
            ...normalizedTemplate,
            name: templateName,
            description: templateDescription
        })

        try {
            setMovingTemplateId(template.id)
            setFeedback(null)

            const response = await fetch(`/saved-attack-templates/${template.id}`, {
                method: 'PUT',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    platform: nextPlatform,
                    name: templateName,
                    description: templateDescription,
                    template: portableTemplate
                })
            })
            const payload = await response.json().catch(() => ({}))
            if (!response.ok)
                throw new Error(payload?.error || `Template move request failed with ${response.status}`)

            setTemplates(previousTemplates =>
                previousTemplates.filter(entry => Number(entry?.id) !== Number(template.id))
            )
            setFeedback({ severity: 'success', messageKey: 'Attack template moved.' })
        }
        catch (error) {
            console.warn('Failed to move attack template', error)
            setFeedback(error?.message ?
                { severity: 'error', message: error.message, translate: false } :
                { severity: 'error', messageKey: 'Could not move attack template.' })
        }
        finally {
            setMovingTemplateId(null)
        }
    }

    const handleExportDraft = () => {
        const payload = {
            exportedAt: new Date().toISOString(),
            platform: activeTemplatePlatform,
            template: normalizedDraftTemplate
        }
        const filenameBase = sanitizeTemplateFilename(draftTemplateName) || 'attack-template'
        const blob = new Blob([JSON.stringify(payload, null, 2)], {
            type: 'application/json;charset=utf-8'
        })
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement('a')

        anchor.href = url
        anchor.download = `${filenameBase}.json`
        anchor.click()

        window.setTimeout(() => URL.revokeObjectURL(url), 0)
        setFeedback({ severity: 'success', messageKey: 'Attack template exported.' })
    }

    const handleImportDraft = async event => {
        const file = event.target.files?.[0]
        if (!file)
            return

        try {
            const imported = JSON.parse(await file.text())
            const importedTemplate =
                imported?.template && typeof imported.template === 'object' && !Array.isArray(imported.template) ?
                    imported.template :
                    imported

            if (!importedTemplate || typeof importedTemplate !== 'object' || Array.isArray(importedTemplate))
                throw new Error('Invalid template file')

            setActiveTemplatePlatform(normalizeAttackTemplatePlatform(imported?.platform ?? importedTemplate?.platform))
            setDraftTemplate(normalizeAttackTemplateDraft(importedTemplate))
            setFeedback({ severity: 'info', messageKey: 'Imported template draft. Review it and press Save.' })
        }
        catch (error) {
            console.warn('Failed to import attack template file', error)
            setFeedback({ severity: 'error', messageKey: 'Could not import attack template file.' })
        }
        finally {
            event.target.value = ''
        }
    }

    const handleImportPresetSelect = React.useCallback(presetValue => {
        const normalizedPresetValue = String(presetValue ?? '')
        setImportPresetValue(normalizedPresetValue)

        if (!normalizedPresetValue)
            return

        const selectedPreset = (Array.isArray(importPresets) ? importPresets : [])
            .find(preset => String(preset?.value ?? '') === normalizedPresetValue)

        if (!selectedPreset || selectedPreset?.isAvailable === false)
            return

        setDraftTemplate(currentTemplate =>
            applyGamePresetToTemplateDraft(
                normalizeAttackTemplateDraft(currentTemplate),
                importTargetWaveId || normalizedDraftTemplate?.defaultWaveId,
                selectedPreset
            )
        )
        setFeedback({ severity: 'info', messageKey: 'Imported preset into the selected wave.' })
        setImportPresetValue('')
    }, [importPresets, importTargetWaveId, normalizedDraftTemplate?.defaultWaveId])

    return (
        <Box sx={{ display: 'grid', gap: 2.5 }}>
            <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', lg: 'center' }}
                justifyContent="space-between"
            >
                <Typography variant="h3" sx={{ fontSize: { xs: '2rem', md: '2.35rem' }, letterSpacing: 0 }}>
                    {t('Attack Templates')}
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <Stack
                        direction="row"
                        spacing={0.75}
                        sx={{
                            p: 0.45,
                            borderRadius: 1.6,
                            border: '1px solid rgba(255,255,255,0.09)',
                            backgroundColor: 'rgba(255,255,255,0.04)'
                        }}
                    >
                        {TEMPLATE_PLATFORMS.map(option => {
                            const active = activeTemplatePlatform === option.value

                            return (
                                <Button
                                    key={option.value}
                                    variant={active ? 'contained' : 'text'}
                                    onClick={() => setActiveTemplatePlatform(option.value)}
                                    sx={{
                                        minHeight: 40,
                                        minWidth: 74,
                                        borderRadius: 1.2,
                                        color: active ? 'text.primary' : 'text.secondary',
                                        backgroundColor: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            backgroundColor: active ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.07)',
                                            boxShadow: 'none'
                                        }
                                    }}
                                >
                                    {option.label}
                                </Button>
                            )
                        })}
                    </Stack>
                    <TextField
                        placeholder={t('Search by name or user')}
                        value={searchTerm}
                        onChange={event => React.startTransition(() => setSearchTerm(event.target.value))}
                        sx={{
                            minWidth: { sm: 280 },
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255,255,255,0.05)'
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRoundedIcon sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<AddRoundedIcon />}
                        onClick={handleCreateTemplate}
                        sx={{
                            minWidth: { xs: '100%', sm: 168 },
                            backgroundColor: 'rgba(255,255,255,0.11)',
                            color: 'text.primary',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.16)',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        {t('Create Template')}
                    </Button>
                </Stack>
            </Stack>

            {feedback && (
                <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ borderRadius: 2.5 }}>
                    {renderFeedbackMessage(feedback)}
                </Alert>
            )}

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.25}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{
                    p: 1.5,
                    borderRadius: 2.5,
                    border: '1px solid rgba(255,255,255,0.08)',
                    backgroundColor: 'rgba(255,255,255,0.03)'
                }}
            >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {`${t('Visible templates')}: ${filteredTemplates.length}`}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {`${activeTemplatePlatform === 'e4k' ? t('E4K Templates') : t('GGE Templates')}: ${templates.length}`}
                </Typography>
            </Stack>

            {loading ? (
                <Box
                    sx={{
                        minHeight: 260,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.08)',
                        backgroundColor: 'rgba(255,255,255,0.03)'
                    }}
                >
                    <CircularProgress color="inherit" />
                </Box>
            ) : filteredTemplates.length === 0 ? (
                <Card sx={{ minHeight: 240 }}>
                    <CardContent
                        sx={{
                            p: 4,
                            height: '100%',
                            minHeight: 240,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            textAlign: 'center'
                        }}
                    >
                        <Typography variant="h5">{t('No templates yet')}</Typography>
                        <Typography variant="body2" sx={{ mt: 0.9, color: 'text.secondary' }}>
                            {t('Create your first attack template to see it here.')}
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={2.5}>
                    {filteredTemplates.map(template => {
                        const templatePlatform = normalizeAttackTemplatePlatform(template?.platform)
                        const moveLabel = templatePlatform === 'e4k' ? t('Move to GGE') : t('Move to E4K')

                        return (
                            <Grid item xs={12} md={6} xl={4} key={template.id}>
                                <Card
                                    sx={{
                                        minHeight: 210,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <CardContent sx={{ p: 3.2 }}>
                                        <Stack spacing={1.4}>
                                            <Typography variant="h5" sx={{ fontSize: '1.15rem' }}>
                                                {template?.name || `${t('Attack Templates')} #${template?.id ?? ''}`}
                                            </Typography>
                                            {showOwnerName && (
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {`${t('Saved by')} ${template?.ownerUsername || accountInfo?.username || t('Unknown user')}`}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
                                                {normalizeAttackTemplatePlatform(template?.platform).toUpperCase()}
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                                {template?.description || t('No description')}
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                    <CardActions sx={{ px: 3.2, pb: 3, pt: 0 }}>
                                        <Stack direction="row" spacing={1.1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<EditRoundedIcon />}
                                                onClick={() => handleEditTemplate(template)}
                                            >
                                                {t('Edit')}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                startIcon={<SwapHorizRoundedIcon />}
                                                onClick={() => handleMoveTemplatePlatform(template)}
                                                disabled={movingTemplateId === template.id}
                                            >
                                                {moveLabel}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="error"
                                                startIcon={<DeleteOutlineRoundedIcon />}
                                                onClick={() => handleDeleteTemplate(template)}
                                                disabled={deletingTemplateId === template.id}
                                                sx={{
                                                    boxShadow: 'none',
                                                    '&:hover': { boxShadow: 'none' }
                                                }}
                                            >
                                                {t('Delete')}
                                            </Button>
                                        </Stack>
                                    </CardActions>
                                </Card>
                            </Grid>
                        )
                    })}
                </Grid>
            )}

            <Dialog
                open={editorOpen}
                onClose={closeEditor}
                fullWidth
                maxWidth={false}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(6, 10, 18, 0.76)',
                        backdropFilter: 'blur(10px)'
                    }
                }}
                PaperProps={{
                    sx: {
                        m: 0,
                        width: 'min(calc(100vw - 80px), 1826px)',
                        maxWidth: '1826px',
                        height: 'min(calc(100vh - 96px), 812px)',
                        maxHeight: '812px',
                        borderRadius: '14px',
                        background: 'linear-gradient(180deg, rgba(33,45,70,0.98) 0%, rgba(27,39,63,0.98) 100%)',
                        border: '1px solid rgba(127, 149, 186, 0.22)',
                        boxShadow: '0 32px 90px rgba(0,0,0,0.55)',
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        px: 3.5,
                        pt: 2.55,
                        pb: 1.05
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 800, fontSize: '1.3rem', letterSpacing: '-0.02em' }}>
                        {editorMode === 'edit' ? t('Edit Template') : t('Create Template')}
                    </Typography>
                    <IconButton onClick={closeEditor} sx={{ color: 'text.secondary', mr: -0.5 }}>
                        <CloseRoundedIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                </DialogTitle>
                <input
                    ref={importInputRef}
                    type="file"
                    accept=".json,application/json"
                    hidden
                    onChange={handleImportDraft}
                />
                <DialogContent
                    sx={{
                        background: 'transparent',
                        px: { xs: 2, md: 3.5 },
                        pb: 1.5,
                        pt: 0.4,
                        overflowY: 'scroll'
                    }}
                >
                    <AttackTemplateEditor
                        parentKey="library"
                        userPlugins={{}}
                        onChange={() => {}}
                        presetOptions={presetOptions}
                        libraryImportControls={{
                            sourceUserOptions: platformSourceUserOptions,
                            selectedSourceUserId,
                            onSourceUserChange: setSelectedSourceUserId,
                            importPresetOptions: normalizeLivePresetImportOptions(importPresets),
                            selectedImportPresetValue: importPresetValue,
                            onImportPresetSelect: handleImportPresetSelect,
                            importTargetWaveId,
                            onImportTargetWaveChange: setImportTargetWaveId,
                            loadingImportPresets,
                            importPresetError
                        }}
                        selectedUser={selectedSourceUser?.isOnline ? selectedSourceUser : null}
                        availableInventory={selectedSourceUserInventory}
                        appLanguage={appLanguage}
                        templateValue={draftTemplate}
                        onTemplateChange={setDraftTemplate}
                    />
                </DialogContent>
                <DialogActions
                    sx={{
                        px: { xs: 2, md: 3.5 },
                        py: 1.45,
                        borderTop: '1px solid rgba(255,255,255,0.08)',
                        justifyContent: 'space-between',
                        gap: 1
                    }}
                >
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                            variant="outlined"
                            startIcon={<UploadRoundedIcon />}
                            onClick={() => importInputRef.current?.click()}
                            sx={{
                                minWidth: 92,
                                minHeight: 38,
                                borderRadius: 1.6,
                                textTransform: 'none',
                                fontWeight: 700
                            }}
                        >
                            {t('Import')}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadRoundedIcon />}
                            onClick={handleExportDraft}
                            sx={{
                                minWidth: 92,
                                minHeight: 38,
                                borderRadius: 1.6,
                                textTransform: 'none',
                                fontWeight: 700
                            }}
                        >
                            {t('Export')}
                        </Button>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                            variant="outlined"
                            onClick={closeEditor}
                            sx={{
                                minWidth: 90,
                                minHeight: 38,
                                borderRadius: 1.6
                            }}
                        >
                            {t('Cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveTemplate}
                            disabled={saving || !draftTemplateName}
                            sx={{
                                minWidth: 72,
                                minHeight: 38,
                                borderRadius: 1.6,
                                boxShadow: 'none',
                                '&:hover': {
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            {t('Save')}
                        </Button>
                    </Stack>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
